'use client'

import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'

import { useIsMobileLayout } from '@/hooks/use-screen'
import { type StreamIntervalMode, streamIntervalModeAtom } from '@/state/storage'
import { ClientMessage, StreamInterval } from '@/types/generated/public_api'

/**
 * Per-connection market-data cadence (public-api SetStreamInterval). The server sends public
 * market data — order books, tickers, trades, analytics — every 250 ms by default, or once a
 * second on request; the user's own orders, fills and positions always stay at 250 ms.
 *
 * The server does not remember the choice: every new socket starts at 250 ms, so it is sent
 * on every connect and reconnect, and again whenever it changes (it takes effect at once,
 * with no reply).
 */
export function resolveStreamInterval(
  mode: StreamIntervalMode,
  { isMobileLayout, saveData }: { isMobileLayout: boolean; saveData: boolean }
): StreamInterval {
  if (mode === 'realtime') return StreamInterval.STREAM_INTERVAL_MS_250
  if (mode === 'saver') return StreamInterval.STREAM_INTERVAL_MS_1000
  // Auto: a phone (or a browser in data-saver mode) gets one frame a second.
  return isMobileLayout || saveData
    ? StreamInterval.STREAM_INTERVAL_MS_1000
    : StreamInterval.STREAM_INTERVAL_MS_250
}

export function isDataSaverInterval(interval: StreamInterval): boolean {
  return interval === StreamInterval.STREAM_INTERVAL_MS_1000
}

export function encodeSetStreamInterval(interval: StreamInterval): Uint8Array<ArrayBuffer> {
  const message = ClientMessage.create({ setStreamInterval: { interval } })
  return new Uint8Array(ClientMessage.encode(message).finish())
}

type NetworkInformationLike = EventTarget & { saveData?: boolean }

function readSaveData(): boolean {
  if (typeof navigator === 'undefined') return false
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection
  return Boolean(connection?.saveData)
}

/** The browser's Save-Data hint (Chrome / Android; absent elsewhere, read as off). */
function useSaveData(): boolean {
  const [saveData, setSaveData] = useState(false)
  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection
    const update = () => setSaveData(readSaveData())
    update()
    connection?.addEventListener?.('change', update)
    return () => connection?.removeEventListener?.('change', update)
  }, [])
  return saveData
}

/** The cadence this device's sockets should run at, from the setting and the device. */
export function useEffectiveStreamInterval(): StreamInterval {
  const mode = useAtomValue(streamIntervalModeAtom)
  const isMobileLayout = useIsMobileLayout()
  const saveData = useSaveData()
  return resolveStreamInterval(mode, { isMobileLayout, saveData })
}
